import { type QuoteFetchProcess } from "#src/composables/quote-fetch.composable.ts";
import { type Quote, type QuoteHint } from "#src/types/Quote.ts";
import { generateUuid, getEnv } from "#src/util/helpers.ts";
import { type Pinia } from "pinia";
import type Pusher from "pusher-js";
import { PusherMock } from "pusher-js-mock";

async function getNewPusherInstance(): Promise<Pusher | PusherMock> {
  const env = getEnv();
  if (env.VITE_MOCK_PUSHER) {
    const pusherMock = await import("pusher-js-mock");
    return new pusherMock.PusherMock();
  }
  const Pusher = await import("pusher-js");

  const instance = new Pusher.default(env.VITE_PUSHER_KEY, {
    enabledTransports: ["ws"],
    forceTLS: true,
    cluster: "mt1",
  });

  if (env.VITE_CYPRESS_E2E) {
    (window as any).Pusher = instance;
  }

  return instance;
}

export async function initSocketForQuotes(
  pinia: Pinia,
  callbacks: {
    fetchQuotesHandler: (uuid: string) => void;
    responseHandler?: (quote: Quote) => void;
    startHandler?: (processes: QuoteFetchProcess[]) => void;
    finishHandler?: (process: QuoteFetchProcess) => void;
    hintHandler?: (hint: QuoteHint) => void;
    endHandler?: () => void;
    failHandler?: () => void;
    subscriptionErrorHandler?: () => void;
  }
): Promise<Pusher | PusherMock> {
  let uuid = generateUuid();
  const env = getEnv();
  if (env.VITE_MOCK_PUSHER) {
    const testHelpers = await import("#src/stores/test-helpers.ts");
    uuid = testHelpers.useTestHelpers(pinia).getUuid();
  }
  const pusher = await getNewPusherInstance();

  const channel = pusher.subscribe(`quote-and-apply@${uuid}`);
  channel.bind("pusher:subscription_succeeded", () => {
    callbacks.fetchQuotesHandler(uuid);
    channel.unbind("pusher:subscription_succeeded");
  });

  if (callbacks.responseHandler) {
    channel.bind("quote:response", callbacks.responseHandler);
  }
  if (callbacks.startHandler) {
    channel.bind("quote:start", callbacks.startHandler);
  }
  if (callbacks.finishHandler) {
    channel.bind("quote:finish", callbacks.finishHandler);
  }
  if (callbacks.hintHandler) {
    channel.bind("quote:hint", callbacks.hintHandler);
  }
  if (callbacks.endHandler) {
    channel.bind("quote:end", callbacks.endHandler);
  }
  if (callbacks.failHandler) {
    channel.bind("quote:fail", callbacks.failHandler);
  }
  if (callbacks.subscriptionErrorHandler) {
    channel.bind(
      "pusher:subscription_error",
      callbacks.subscriptionErrorHandler
    );
  }

  return pusher;
}

export async function initSocketForIllustration(
  eAppUuid: string,
  callbacks: {
    fetchIllustrationHandler: () => void;
    endHandler?: () => void;
    failHandler?: () => void;
  }
): Promise<Pusher | PusherMock> {
  const pusher = await getNewPusherInstance();

  const channel = pusher.subscribe(`quote-and-apply@${eAppUuid}`);
  channel.bind("pusher:subscription_succeeded", () => {
    callbacks.fetchIllustrationHandler();
    channel.unbind("pusher:subscription_succeeded");
  });

  if (callbacks.endHandler) {
    channel.bind("illustration:end", callbacks.endHandler);
  }
  if (callbacks.failHandler) {
    channel.bind("quote:fail", callbacks.failHandler);
  }

  return pusher;
}

export async function initSocketForSignup(
  npn: string,
  callbacks: {
    createUserHandler: () => void;
    readyHandler: ({ ready, url }: { ready: boolean; url: string }) => void;
  }
): Promise<Pusher | PusherMock> {
  const pusher = await getNewPusherInstance();

  const channel = pusher.subscribe(`npn-${npn}`);
  channel.bind("pusher:subscription_succeeded", () => {
    callbacks.createUserHandler();
    channel.unbind("pusher:subscription_succeeded");
  });
  channel.bind("approved-domain-ready", callbacks.readyHandler);

  return pusher;
}

export async function initSocketForEnvelope(
  uuid: string,
  callbacks: {
    startProcessHandler: () => void;
    readyHandler?: () => void;
    errorHandler?: () => void;
    startHandler?: () => void;
  }
): Promise<Pusher | PusherMock> {
  const pusher = await getNewPusherInstance();

  const channel = pusher.subscribe(`strife.envelope-${uuid}`);
  channel.bind("pusher:subscription_succeeded", () => {
    callbacks.startProcessHandler();
    channel.unbind("pusher:subscription_succeeded");
  });
  if (callbacks.startHandler) channel.bind("start", callbacks.startHandler);
  if (callbacks.readyHandler) channel.bind("ready", callbacks.readyHandler);
  if (callbacks.errorHandler) channel.bind("error", callbacks.errorHandler);

  return pusher;
}

export async function initSocketForInsuredSigned(
  uuid: string,
  callbacks: {
    getEnvelopeHandler: () => void;
    insuredSignedHandler: (v: string) => void;
    insuredDeclinedHandler: (v: string) => void;
  }
): Promise<Pusher | PusherMock> {
  const pusher = await getNewPusherInstance();

  const channel = pusher.subscribe(`strife.insured_signed-${uuid}`);
  channel.bind("pusher:subscription_succeeded", () => {
    callbacks.getEnvelopeHandler();
    channel.unbind("pusher:subscription_succeeded");
  });
  // eslint-disable-next-line
  channel.bind("insured-signed", (v: string) => {
    if (v === "decline") return callbacks.insuredDeclinedHandler(v);
    return callbacks.insuredSignedHandler(v);
  });

  return pusher;
}

export async function initSocketForSubmission(
  pinia: Pinia,
  callbacks: {
    submitEappHandler: (uuid: string) => any;
    readyHandler: (args: { carrier_eapp_url?: string }) => any;
    errorHandler: (args: any) => any;
  }
): Promise<Pusher | PusherMock> {
  let uuid = generateUuid();
  const pusher = await getNewPusherInstance();

  const env = getEnv();
  if (env.VITE_MOCK_PUSHER) {
    const testHelpers = await import("#src/stores/test-helpers.ts");
    uuid = testHelpers.useTestHelpers(pinia).getUuid();
  }

  const channel = pusher.subscribe(`strife.submission-${uuid}`);
  channel.bind("pusher:subscription_succeeded", () => {
    callbacks.submitEappHandler(uuid);
    channel.unbind("pusher:subscription_succeeded");
  });

  if (callbacks.readyHandler) channel.bind("ready", callbacks.readyHandler);
  if (callbacks.errorHandler) channel.bind("error", callbacks.errorHandler);

  return pusher;
}
